import { Platform, StyleSheet, View, ColorValue, ViewProps } from "react-native";
import React, { forwardRef, ReactNode, useMemo } from "react";
import { useTheme } from "../hooks/use-theme";
import Markdown, {
    RenderFunction,
    MarkdownIt,
    ASTNode,
} from "@code-atlas/react-native-markdown-display";
import hasParents from "@code-atlas/react-native-markdown-display/src/lib/util/hasParents";
import textStyleProps from "@code-atlas/react-native-markdown-display/src/lib/data/textStyleProps";
import openUrl from "@code-atlas/react-native-markdown-display/src/lib/util/openUrl";
import Text from "./Text";
import InfoButton from "./InfoButton";
import merge from "lodash/merge";

export type MarkdownTextProps = {
    markdown: string;
    onLinkPress?: (url: string) => boolean;
    markdownStyle?: StyleSheet.NamedStyles<unknown>;
    allowFontScaling?: boolean;
    fontColor?: ColorValue;
} & ViewProps;

type MdStaleProps = {
    fontColor?: ColorValue;
};

export const useNormalMarkdownStyle = ({ fontColor }: MdStaleProps) => {
    const { fonts, gridSize, colors } = useTheme();
    return useMemo(() => {
        return {
            // The main container

            // @pseudo class, does not have a unique render rule
            heading1: {
                marginBottom: gridSize / 4,
                color: fontColor,
                ...fonts.titleLarge,
                flexDirection: "row",
                textAlign: "center",
            },
            heading2: {
                marginBottom: gridSize / 4,
                color: fontColor,
                ...fonts.titleMedium,
                flexDirection: "row",
            },
            heading3: {
                marginBottom: gridSize / 4,
                color: fontColor,
                ...fonts.titleSmall,
                flexDirection: "row",
            },
            heading4: {
                color: fontColor,
                marginBottom: gridSize / 4,
                ...fonts.bodyLarge,
                flexDirection: "row",
            },
            heading5: {
                color: fontColor,
                marginBottom: gridSize / 4,
                ...fonts.bodyMedium,
                flexDirection: "row",
            },
            heading6: {
                color: fontColor,
                marginBottom: gridSize / 4,
                ...fonts.bodySmall,
                flexDirection: "row",
            },
            bullet_list: {
                color: fontColor,
                ...fonts.bodyMedium,
                lineHeight: 24,
            },
            ordered_list: {
                color: fontColor,
                ...fonts.bodyMedium,
                lineHeight: 24,
                textAlign: "center",
            },
            paragraph: {
                marginTop: gridSize / 4,
                marginBottom: 0,
                ...fonts.bodyMedium,
                color: fontColor,
                textAlign: "center",
            },
            bold: {
                ...fonts.bodyLarge,
                textAlign: "center",
            },
            strong: {
                ...fonts.bodyLarge,
                textAlign: "center",
            },
            bullet_list_icon: {
                marginLeft: 5,
                marginRight: 10,
            },
            hardbreak: {
                width: "100%",
                height: 1,
            },
            link: {
                color: colors.secondary,
            },
            blocklink: {
                color: colors.secondary,
            },
            helpButton: {
                margin: 0,
                marginHorizontal: 4,
                borderColor: colors.onWarningBackground,
                borderWidth: 1,
                borderRadius: 8,
                backgroundColor: colors.warningBackground,
                textAlign: "center",
            },
        };
    }, [fontColor, fonts, colors, gridSize]);
};

const handleHtml: RenderFunction = (
    node: ASTNode,
    children: ReactNode[],
    parent: ASTNode[],
    styles,
) => {
    if (node.content.trim() === "<br>") {
        return (
            <Text style={styles.hardbreak} key={node.key}>
                {""}
            </Text>
        );
    }

    return null;
};

type GetRulesParams = {
    allowFontScaling?: boolean;
};

const getRules = ({ allowFontScaling }: GetRulesParams) => {
    return {
        list_item: (node, children, parent, styles, inheritedStyles = {}) => {
            // we need to grab any text specific stuff here that is applied on the list_item style
            // and apply it onto bullet_list_icon. the AST renderer has some workaround code to make
            // the content classes apply correctly to the child AST tree items as well
            // as code that forces the creation of the inheritedStyles object for list_items
            const refStyle: Record<string, string> = {
                ...inheritedStyles,
                ...StyleSheet.flatten(styles.list_item),
            };

            const arr: string[] = Object.keys(refStyle);

            const modifiedInheritedStylesObj: Record<string, string> = {};

            for (let b = 0; b < arr.length; b++) {
                if (textStyleProps.includes(arr[b])) {
                    modifiedInheritedStylesObj[arr[b]] = refStyle[arr[b]];
                }
            }

            if (hasParents(parent, "bullet_list")) {
                return (
                    <View key={node.key} style={styles._VIEW_SAFE_list_item}>
                        <Text
                            style={[modifiedInheritedStylesObj, styles.bullet_list_icon]}
                            accessible={false}
                            allowFontScaling={allowFontScaling}
                        >
                            {Platform.select({
                                android: "\u2022",
                                ios: "\u2022", //"\u25cf",
                                default: "\u2022",
                            })}
                        </Text>
                        <View style={[styles._VIEW_SAFE_bullet_list_content, styles.bullet_list]}>
                            {children}
                        </View>
                    </View>
                );
            }

            if (hasParents(parent, "ordered_list")) {
                const orderedListIndex = parent.findIndex((el) => el.type === "ordered_list");

                const orderedList = parent[orderedListIndex];
                let listItemNumber;

                if (orderedList.attributes && orderedList.attributes.start) {
                    listItemNumber = orderedList.attributes.start + node.index;
                } else {
                    listItemNumber = node.index + 1;
                }

                return (
                    <View key={node.key} style={styles._VIEW_SAFE_list_item}>
                        <Text
                            style={[modifiedInheritedStylesObj, styles.ordered_list_icon]}
                            allowFontScaling={allowFontScaling}
                        >
                            {listItemNumber}
                            {node.markup}
                        </Text>
                        <View style={styles._VIEW_SAFE_ordered_list_content}>{children}</View>
                    </View>
                );
            }

            // we should not need this, but just in case
            return (
                <View key={node.key} style={styles._VIEW_SAFE_list_item}>
                    {children}
                </View>
            );
        },
        link: (node, children, parent, styles, onLinkPress: (url: string) => boolean) => {
            const href: string = node.attributes.href;
            if (href.startsWith("help:")) {
                return <InfoButton key={node.key} onPress={() => openUrl(href, onLinkPress)} />;
            }
            return (
                <Text
                    key={node.key}
                    style={styles.link}
                    onPress={() => openUrl(href, onLinkPress)}
                    allowFontScaling={allowFontScaling}
                >
                    {children}
                </Text>
            );
        },
        text: (node, _1, _2, _3, style) => {
            return (
                <Text key={node.key} style={style} allowFontScaling={allowFontScaling}>
                    {node.content}
                </Text>
            );
        },
        textgroup: (node, children, parent, styles) => (
            <Text key={node.key} style={styles.textgroup} allowFontScaling={allowFontScaling}>
                {children}
            </Text>
        ),
        html_block: handleHtml,
        html_inline: handleHtml,
    } as { [key: string]: RenderFunction };
};

// eslint-disable-next-line react/display-name
const MarkdownView = forwardRef<View, MarkdownTextProps>(
    ({ markdown, onLinkPress, markdownStyle, fontColor, allowFontScaling, ...props }, ref) => {
        const mdStyle = useNormalMarkdownStyle({ fontColor });
        const style = useMemo(() => merge(mdStyle, markdownStyle), [markdownStyle, mdStyle]);
        const rules = useMemo(() => getRules({ allowFontScaling }), [allowFontScaling]);

        return (
            <View ref={ref} {...props}>
                {/* Markdown forgot to add children properties*/}
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <Markdown
                    /* eslint-disable-next-line @typescript-eslint/no-unsafe-call */
                    markdownit={MarkdownIt({
                        html: true,
                        typographer: true,
                        linkify: false,
                        breaks: true,
                    })}
                    style={style}
                    onLinkPress={onLinkPress}
                    rules={rules}
                    mergeStyle={false}
                >
                    {markdown}
                </Markdown>
            </View>
        );
    },
);

export default MarkdownView;
